import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";

const initialState = {
  gapGamesByProvider: {},
  qtechGamesByProvider: {},
  loading: false,
  errorByProvider: {},
  casinoConversionRate: null,
  casinoConversionRateLoading: false,
  casinoConversionRateError: null,
};

const extractGamesArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  const nested =
    payload?.games ??
    payload?.gameList ??
    payload?.game_list ??
    payload?.items ??
    payload?.list ??
    payload?.records ??
    payload?.result?.games ??
    payload?.result?.items ??
    payload?.data?.games ??
    payload?.data?.items ??
    payload?.Data?.games;
  if (Array.isArray(nested)) return nested;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeGapGamesResponse = (response) => {
  const list = extractGamesArray(response);
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((item) => ({
    gameId: item.gameId ?? item.game_id ?? item.id?.toString() ?? "",
    gameCode: item.gameCode ?? item.game_code ?? item.code ?? "",
    gameName: item.gameName ?? item.game_name ?? item.name ?? item.title ?? "",
    urlThumb:
      item.urlThumb ??
      item.url_thumb ??
      item.image_url ??
      item.imageUrl ??
      item.thumb ??
      "",
    category: item.category ?? "ALL",
    providerName: item.providerName ?? item.provider_name ?? "",
    subProviderName: item.subProviderName ?? item.sub_provider_name ?? "",
    superProviderName: item.superProviderName ?? item.super_provider_name ?? "",
    providerTag: item.providerTag ?? item.provider_tag ?? "",
    priority: item.priority ?? 0,
    ...item,
  }));
};

const normalizeQtechGamesResponse = (response, providerCode = "") => {
  const list = extractGamesArray(response);
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((item) => {
    const images = item.images ?? [];
    const image =
      images.find((img) => img?.type === "logo-square" || img?.type === "banner") ||
      images[0] ||
      {};
    const providerName = item?.provider?.name ?? providerCode;
    return {
      gameId: item.id ?? item.gameId ?? "",
      gameCode: item.id ?? item.gameCode ?? "",
      gameName: item.name ?? item.gameName ?? "",
      urlThumb: image.url ?? image.src ?? item.urlThumb ?? item.image_url ?? "",
      category: item.category ?? "ALL",
      providerName,
      subProviderName: providerName,
      superProviderName: providerName,
      providerTag: providerName,
      priority: item.priority ?? 0,
      ...item,
    };
  });
};

const pickLaunchUrlFromPayload = (payload) => {
  if (!payload) return "";
  if (typeof payload === "string") return payload;
  const nested = payload?.data ?? payload?.result ?? payload?.Result ?? payload?.body ?? null;
  const candidates = [
    payload.launchUrl,
    payload.launch_url,
    payload.url,
    payload.gameUrl,
    payload.game_url,
    nested?.launchUrl,
    nested?.launch_url,
    nested?.url,
    nested?.gameUrl,
  ];
  const found = candidates.find((v) => typeof v === "string" && v.trim());
  return found ? found.trim() : "";
};

export const fetchGapCasinoGames = createAsyncThunk(
  "casino/fetchGapCasinoGames",
  async ({ providerName = "MAC88", page = 1 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/gap-casino/gamesList", {
        provider_name: providerName,
        page,
      });
      return {
        providerName,
        games: normalizeGapGamesResponse(response),
      };
    } catch (error) {
      return rejectWithValue({
        providerName,
        error: error?.response?.data || error?.message || "Failed to fetch gap-casino games",
      });
    }
  },
);

export const fetchQtechGames = createAsyncThunk(
  "casino/fetchQtechGames",
  async ({ providerCode } = {}, { rejectWithValue }) => {
    try {
      const code = (providerCode || "").trim();
      if (!code) return { providerCode: "", games: [] };
      const response = await axiosInstance.get(
        `/qtech/gameList?providers=${encodeURIComponent(code)}`,
      );
      return {
        providerCode: code,
        games: normalizeQtechGamesResponse(response, code),
      };
    } catch (error) {
      return rejectWithValue({
        providerCode,
        error: error?.response?.data || error?.message || "Failed to fetch qtech games",
      });
    }
  },
);

export const fetchGapCasinoLaunchUrl = createAsyncThunk(
  "casino/fetchGapCasinoLaunchUrl",
  async ({ category, gameId, providerName = "MAC88" } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/gap-casino/launchUrl", {
        category: category || "ALL",
        game_id: String(gameId || ""),
        provider_name: providerName,
      });
      return { launchUrl: pickLaunchUrlFromPayload(response) };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to fetch gap launch url");
    }
  },
);

export const fetchQtechLaunchUrl = createAsyncThunk(
  "casino/fetchQtechLaunchUrl",
  async ({ providerCode, gameId } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/qtech/launchUrl", {
        providerCode: (providerCode || "").trim(),
        gameId: String(gameId || ""),
      });
      return { launchUrl: pickLaunchUrlFromPayload(response) };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || "Failed to fetch qtech launch url");
    }
  },
);

export const fetchCasinoConversionRate = createAsyncThunk(
  "casino/fetchCasinoConversionRate",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/getCasinoConversionRate");
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error?.message || "Failed to fetch casino conversion rate",
      );
    }
  },
);

const casinoSlice = createSlice({
  name: "casino",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGapCasinoGames.pending, (state, action) => {
        const cacheKey = action.meta.arg?.requestKey || action.meta.arg?.providerName || "MAC88";
        state.loading = true;
        state.errorByProvider[cacheKey] = null;
      })
      .addCase(fetchGapCasinoGames.fulfilled, (state, action) => {
        const { requestKey, games } = action.payload || {};
        const metaKey = action.meta.arg?.requestKey || action.meta.arg?.providerName || "MAC88";
        const key = requestKey || metaKey;
        if (!key) return;
        state.loading = false;
        state.gapGamesByProvider[key] = Array.isArray(games) ? games : [];
        state.errorByProvider[key] = null;
      })
      .addCase(fetchGapCasinoGames.rejected, (state, action) => {
        const providerName =
          action.meta.arg?.requestKey ||
          action.payload?.providerName ||
          action.meta.arg?.providerName ||
          "MAC88";
        state.loading = false;
        state.errorByProvider[providerName] = action.payload?.error || action.error?.message || "Request failed";
        if (!Array.isArray(state.gapGamesByProvider[providerName])) {
          state.gapGamesByProvider[providerName] = [];
        }
      })
      .addCase(fetchQtechGames.pending, (state, action) => {
        const providerCode = action.meta.arg?.providerCode || "";
        if (!providerCode) return;
        state.loading = true;
        state.errorByProvider[providerCode] = null;
      })
      .addCase(fetchQtechGames.fulfilled, (state, action) => {
        const { providerCode, games } = action.payload || {};
        if (!providerCode) return;
        state.loading = false;
        state.qtechGamesByProvider[providerCode] = Array.isArray(games) ? games : [];
        state.errorByProvider[providerCode] = null;
      })
      .addCase(fetchQtechGames.rejected, (state, action) => {
        const providerCode = action.payload?.providerCode || action.meta.arg?.providerCode || "";
        if (!providerCode) return;
        state.loading = false;
        state.errorByProvider[providerCode] = action.payload?.error || action.error?.message || "Request failed";
        if (!Array.isArray(state.qtechGamesByProvider[providerCode])) {
          state.qtechGamesByProvider[providerCode] = [];
        }
      })
      .addCase(fetchCasinoConversionRate.pending, (state) => {
        state.casinoConversionRateLoading = true;
        state.casinoConversionRateError = null;
      })
      .addCase(fetchCasinoConversionRate.fulfilled, (state, action) => {
        state.casinoConversionRateLoading = false;
        state.casinoConversionRate = action.payload || null;
        state.casinoConversionRateError = null;
      })
      .addCase(fetchCasinoConversionRate.rejected, (state, action) => {
        state.casinoConversionRateLoading = false;
        state.casinoConversionRateError = action.payload || action.error?.message || "Request failed";
      });
  },
});

export default casinoSlice.reducer;
